import React, { createContext, useContext, useEffect, useState } from "react";
import { useInitialValues } from "./use-initial-values";
import { mapFiltersToAccommodationRequestQueryInput } from "../utils/mapping";
import { isEqual, omit } from "lodash";
import { UseSearchFilters, useSearchFilters } from "@bonliva-crm/ui/search-filters";
import { AccommodationRequestQueryInput } from "@graphql";
import { AccommodationRequestFilterState } from "../utils/accommodation-request-filter-state";

export type AccommodationRequestFilter = AccommodationRequestQueryInput;

type AccommodationRequestSearchFiltersReturnType = {
  queryInput: AccommodationRequestFilter;
  filterManager: UseSearchFilters<AccommodationRequestFilterState>;
  /**
   * Form has non-empty value in any field
   */
  hasValues: boolean;
};

const AccommodationRequestSearchFiltersContext =
  createContext<AccommodationRequestSearchFiltersReturnType>(null!);

export const useAccommodationRequestSearchFilters = () => {
  const context = useContext(AccommodationRequestSearchFiltersContext);

  if (context == null) {
    throw new Error(
      "useAccommodationRequestSearchFilters must be used within AccommodationRequestSearchFiltersProvider"
    );
  }

  return context;
};

export const AccommodationRequestSearchFiltersProvider = ({
  children
}: {
  children: any;
}) => {
  const { initialValues, emptyValues } = useInitialValues();

  const [queryInput, setQueryInput] = useState<AccommodationRequestFilter>(
    mapFiltersToAccommodationRequestQueryInput(initialValues)
  );

  const [hasValues, setHasValues] = useState(false);

  const filterManager = useSearchFilters({
    initialValues: initialValues,
    localStorageKey: "AccommodationRequestSearchFavoriteFields",
    activeFilters: [
      "senderIds",
      "responsiblePerson",
      "accommodationRequestStatusFilter",
      "createdFromTo"
    ],
    // parseSearchProfileFilters: (f) => f,
    onChange: (value) => {
      setQueryInput(mapFiltersToAccommodationRequestQueryInput(value));
    }
  });

  const values = filterManager.formMethods.watch();

  useEffect(() => {
    const omitFields = ["sort", "text"];
    setHasValues(!isEqual(omit(emptyValues, omitFields), omit(values, omitFields)));
  }, [emptyValues, values]);

  return (
    <AccommodationRequestSearchFiltersContext.Provider
      value={{
        queryInput,
        filterManager,
        hasValues
      }}
    >
      {children}
    </AccommodationRequestSearchFiltersContext.Provider>
  );
};
